'use client'

import React, { useCallback, useMemo, useState } from 'react'
import { useRouter } from 'next/navigation'
import { useDispatch } from 'react-redux'

import { URL_REGEX } from '@/constants/regex'
import { DEFAULT_VCARD_FORM_VALUES } from '@/constants/VCard'
import { updateOnboardingCardPreview } from '@/lib/redux/uiSlice'
import { removeOnboardingData } from '@/lib/onboarding/storage'
import { normalizeUrl } from '@/lib/utils'
import LcIcon from '@/components/livecontact/icons'
import UrlPillForm from './UrlPillForm'

type Props = {
  urlPlaceholder: string
  buttonText: string
  classInput?: string
}

/** Landing hero form — validates the URL inline, then hands the site off to /onboarding/url to scan. */
const HeroUrlForm = ({ urlPlaceholder, buttonText, classInput }: Props) => {
  const [url, setUrl] = useState('')
  const [error, setError] = useState<string | undefined>()
  const router = useRouter()
  const dispatch = useDispatch()

  const disabledUrlBtn = useMemo(() => !url || !URL_REGEX.test(url), [url])

  const handleChange = useCallback((next: string) => {
    setUrl(next)
    setError(undefined)
  }, [])

  const handleUrlSubmit = useCallback(() => {
    if (!url) {
      setError('Enter a website URL, or choose “Enter manually” to continue.')
      return
    }
    if (disabledUrlBtn) {
      setError('That doesn’t look like a valid URL.')
      return
    }

    const normalizedUrl = normalizeUrl(url)
    removeOnboardingData()
    dispatch(updateOnboardingCardPreview(DEFAULT_VCARD_FORM_VALUES))

    router.push(`/onboarding/url?url=${encodeURIComponent(normalizedUrl)}`)
  }, [disabledUrlBtn, router, url, dispatch])

  const handleEnterManually = useCallback(() => {
    removeOnboardingData()
    dispatch(updateOnboardingCardPreview(DEFAULT_VCARD_FORM_VALUES))
    router.push('/onboarding/identity')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="flex w-full max-w-[400px] flex-col items-stretch gap-3">
      <UrlPillForm
        variant="stacked"
        value={url}
        onChange={handleChange}
        onSubmit={handleUrlSubmit}
        placeholder={urlPlaceholder}
        buttonText={buttonText}
        classInput={classInput}
        error={error}
      />

      <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5">
        <span className="inline-flex items-center gap-2.5 text-[12px] font-medium leading-[1.4] tracking-[-0.2px] text-[#757575]">
          <LcIcon name="credit-card" size={16} className="text-[#757575]" />
          No credit card required
        </span>
        <button
          type="button"
          onClick={handleEnterManually}
          className="rounded-[6px] px-1 text-[12px] font-medium tracking-[-0.2px] text-[#757575] underline-offset-4 outline-none transition-colors duration-[140ms] hover:text-ink hover:underline focus-visible:ring-2 focus-visible:ring-brand/40"
        >
          No website? Enter manually
        </button>
      </div>
    </div>
  )
}

export default HeroUrlForm
